/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Injectable, Inject } from '@angular/core';
import _ from 'lodash';

@Injectable({ providedIn: 'root' })
export class FeedStoreService {
  constructor(@Inject('AlertService') private alertService: any) {}

  create(fetchFeed: Function, storeParameters?: object, options: { toast?: boolean } = {}): any {
    const { alertService } = this;
    let latestHeaders: any;
    let inflightPromise: any = null;

    // Use a named constructor so lodash isPlainObject() returns false.
    // ModalCtrl uses _.merge($scope, data) to spread resolved data onto scope —
    // _.merge deep-clones plain objects, which disconnects the feedItems reference
    // and breaks live updates. Non-plain objects are assigned by reference.
    function FeedStoreResult() {}
    const store: any = new (FeedStoreResult as any)();
    store.parameters = {};
    store.fetchFeed = fetchFeed;
    store.feedItems = [];
    store.isLastPage = true;
    store.isLoading = false;
    // True after a fetch fails, until the next one starts, so a page can tell "failed" from "nothing there".
    store.hasFailed = false;

    // Returned only for settled no-op paths (loadOnce when items exist, loadMore on last page).
    const selfPromise = {
      then(callback: Function) { callback(store); },
    };

    const handleResponse = (response: any) => {
      try {
        latestHeaders = response.headers;
        Array.prototype.push.apply(store.feedItems, response.data);
        store.isLastPage = typeof latestHeaders === 'function' ? !latestHeaders('X-Next-Page') : true;
      } finally {
        store.isLoading = false;
        inflightPromise = null;
      }
      return response;
    };

    const handleErrorResponse = (response: any) => {
      if (options.toast !== false) alertService.addDanger(response.statusText);
      store.hasFailed = true;
      store.isLoading = false;
      inflightPromise = null;
    };

    const doFetch = (page?: any) => {
      if (store.isLoading) return inflightPromise;
      const pagination = { page: (page || 1), per_page: 10 };
      const parameters = _.merge({}, pagination, storeParameters, store.parameters);
      store.isLoading = true;
      store.hasFailed = false;
      inflightPromise = store.fetchFeed(parameters).then(handleResponse, handleErrorResponse).then(() => store);
      return inflightPromise;
    };

    store.reset = () => { store.feedItems = []; };
    store.loadOnce = () => (_.isEmpty(store.feedItems) ? store.load() : selfPromise);
    store.load = () => { store.feedItems = []; return doFetch(); };
    store.loadMore = () => {
      if (_.isEmpty(store.feedItems)) return store.load();
      const nextPage = typeof latestHeaders === 'function' ? latestHeaders('X-Next-Page') : undefined;
      if (nextPage) return doFetch(nextPage);
      return selfPromise;
    };

    return store;
  }
}
